/**
 * Empty board — the whole plan (what a container is, how to start: open a
 * campaign group), or one group with nothing on it yet (add one, or create
 * its master config first).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui';

import { TourCta } from '../tutorial/entry/TourCta';

interface Props {
  onAdd: () => void;
  /** Set when one campaign is open. */
  campaignName?: string | null;
  addBlocked?: string | null;
}

export const BoardEmptyState: FC<Props> = ({ onAdd, campaignName = null, addBlocked = null }) =>
  campaignName ? (
    <div className="flex flex-col items-start gap-2 p-6">
      <p className="text-sm font-medium">No container on {campaignName} yet.</p>
      <p className="max-w-xl text-sm text-muted-foreground">
        {addBlocked ?? 'Add one — it follows this campaign group’s master config, and its schedule, slots and lists are its own.'}
      </p>
      {addBlocked ? null : (
        <Button size="sm" variant="outline" onClick={onAdd}>
          <Plus className="mr-1 h-3.5 w-3.5" /> Add container
        </Button>
      )}
    </div>
  ) : (
    <div className="flex flex-col items-start gap-2 p-6">
      <p className="text-sm font-medium">No containers yet.</p>
      <p className="max-w-xl text-sm text-muted-foreground">
        A container is one row of your day-sheet — its campaign, schedule,
        drop slots, list groups and gateways. Open a campaign group with the
        switcher beside the views, create its master config, then add
        containers; the day is laid out top-to-bottom in this order. Generate
        creates drafts only and nothing sends by itself, so it is safe to
        experiment.
      </p>
      <div className="flex flex-wrap gap-2">
        <TourCta />
        {addBlocked ? null : (
          <Button size="sm" variant="outline" onClick={onAdd}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Add container
          </Button>
        )}
      </div>
    </div>
  );
